/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Table,
  InputNumber,
  Input,
  Spin,
  message,
} from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { Loader2, Save } from 'lucide-react';
import { get } from 'lodash';
import moment from 'moment';
import {
  fetchDsrLogPqt,
  updateDsrLogPqt,
} from '../../redux/projectPqtReports/actions';
import { createDsrLog } from '../../redux/dsr/action';
import './PqtActualValuesPage.css';

const toFiniteNumber = (v) => {
  if (v == null || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && /^\s*-?\d+(\.\d+)?\s*$/.test(v)) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Keys that mean “today” is split from cumulative till on the API model. */
const TODAY_OWN_KEYS = [
  'todaysActualDays',
  'TodaysActualDays',
  'todayActualDays',
  'TodayActualDays',
  'todayPqtActual',
  'TodayPqtActual',
  'dailyActualDays',
  'DailyActualDays',
  'actualDaysForLog',
  'ActualDaysForLog',
];

const anyTodayOwnedKey = (record) => {
  if (!record || typeof record !== 'object') return false;
  return TODAY_OWN_KEYS.some((p) => Object.prototype.hasOwnProperty.call(record, p));
};

const TILL_PATHS = [
  /* Legacy / API DSR PQT cumulative column (matches dev “Till Date”). */
  'actualDaysToDate',
  'ActualDaysToDate',
  'actual_days_to_date',
  'tillNoOfDays',
  'tillNoOfDay',
  'TillNoOfDays',
  'TillNoOfDay',
  'noOfDaysTillDate',
  'NoOfDaysTillDate',
  'noOfDaysTillTheDate',
  'NoOfDaysTillTheDate',
  'noOfDaysTill',
  'NoOfDaysTill',
  'noOfDaysPqtTillDate',
  'NoOfDaysPqtTillDate',
  'noOfDaysActualTillDate',
  'NoOfDaysActualTillDate',
  'till_no_of_days',
  'no_of_days_till_date',
  'actualDaysTillDate',
  'ActualDaysTillDate',
  'actual_days_till_date',
  'cumulativeActualDays',
  'CumulativeActualDays',
  'cummulativeActualDays',
  'totalActualDays',
  'TotalActualDays',
  'actualToDate',
  'ActualToDate',
  'daysTillDate',
  'DaysTillDate',
  'daysTill',
  'DaysTill',
  'tillActualDays',
  'TillActualDays',
  'actualTillDate',
  'ActualTillDate',
  'totalTillDate',
  'TotalTillDate',
  'ytdActualDays',
  'YtdActualDays',
  'accumulatedDays',
  'AccumulatedDays',
  'tillDate',
  'TillDate',
  ['pqtElement', 'tillNoOfDays'],
  ['pqtElement', 'noOfDaysTillDate'],
  ['projectPqtElement', 'tillNoOfDays'],
  ['projectPqtReportElement', 'tillNoOfDays'],
  ['projectPqtReportElement', 'noOfDaysTillDate'],
  ['dsrLogPqtElement', 'tillNoOfDays'],
  ['dsrLogPqtElement', 'noOfDaysTillDate'],
  ['element', 'tillNoOfDays'],
  ['element', 'noOfDaysTillDate'],
  ['pqtElement', 'actualDaysToDate'],
  ['projectPqtReportElement', 'actualDaysToDate'],
  ['dsrLogPqtElement', 'actualDaysToDate'],
];

const TILLISH_KEY_RE = /(cumulative|accumulated|total).*(actual|days|day)|^(noOfDays|NoOfDays).*(till|Till|Date)|tillNoOfDays|TillNoOfDays|actualDaysToDate|ActualDaysToDate|actualDaysTill|ActualDaysTill|daysTill|DaysTill|tillTheDate|TillTheDate/i;

const scanRecordForTillLikeNumeric = (record) => {
  if (!record || typeof record !== 'object') return null;
  const keys = Object.keys(record);
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (k.startsWith('__')) continue;
    if (!TILLISH_KEY_RE.test(k)) continue;
    const n = toFiniteNumber(record[k]);
    if (n != null) return n;
  }
  return null;
};

/** Till from named paths + heuristic scan only (never `actualDays` — that may be “today”). */
const pickTillPathsAndScan = (record) => {
  if (!record) return null;
  for (let i = 0; i < TILL_PATHS.length; i += 1) {
    const p = TILL_PATHS[i];
    const raw = Array.isArray(p) ? get(record, p) : (record[p] ?? get(record, p));
    const n = toFiniteNumber(raw);
    if (n != null) return n;
  }
  return scanRecordForTillLikeNumeric(record);
};

/**
 * Snapshot of cumulative till at last GET — includes legacy payloads where only `actualDays`
 * carries cumulative and “today” is a separate concept / empty (matches dev DSR PQT).
 */
const computeTillSnapshotForRow = (el) => {
  const t = pickTillPathsAndScan(el);
  if (t != null) return t;
  if (!anyTodayOwnedKey(el)) {
    const ad = toFiniteNumber(el.actualDays ?? el.ActualDays);
    if (ad != null) return ad;
  }
  return null;
};

/**
 * Controlled value for Actual Days: explicit per-log fields; else `actualDays` unless it only
 * matched cumulative till (same as snapshot and no split keys).
 */
const getActualDaysInputValue = (record) => {
  const adtd = toFiniteNumber(record.actualDaysToDate ?? record.ActualDaysToDate);
  if (adtd != null) {
    const ad = toFiniteNumber(record.actualDays ?? record.ActualDays);
    return ad != null ? ad : undefined;
  }
  let sawTodayKey = false;
  for (let i = 0; i < TODAY_OWN_KEYS.length; i += 1) {
    const p = TODAY_OWN_KEYS[i];
    if (!Object.prototype.hasOwnProperty.call(record, p)) continue;
    sawTodayKey = true;
    const raw = record[p];
    if (raw === null || raw === undefined || raw === '') continue;
    const n = toFiniteNumber(raw);
    if (n != null) return n;
  }
  if (sawTodayKey) {
    return undefined;
  }
  const ad = toFiniteNumber(record.actualDays ?? record.ActualDays);
  if (ad == null) return undefined;
  const snap = record.__tillSnapshot;
  if (snap != null && ad === snap) return undefined;
  return ad;
};

const PqtActualValuesPage = (props) => {
  const {
    selectedProject,
    selectedMainline,
    sowDetails = [],
    selectedDate,
    currentDsrLog,
    dsrLogPqt,
    isDsrLogPqtLoading,
    isDsrLogPqtUpdating,
    lastDsrLogPqtUpdateSuccess,
    onFetchDsrLogPqt,
    onUpdateDsrLogPqt,
    onCreateDsrLog,
    onCancel,
  } = props;

  const [localElements, setLocalElements] = useState([]);
  const [fetchKey, setFetchKey] = useState(0);
  const saveClickedRef = React.useRef(false);

  const projectId = get(selectedProject, 'id');
  // In some DSR flows, `selectedMainline` isn't populated yet when this page mounts.
  // Fall back to the already-created DSR log's sowId so PQT elements can still load.
  const sowId =
    get(selectedMainline, 'id') ??
    get(currentDsrLog, 'sowId') ??
    get(Array.isArray(sowDetails) ? sowDetails[0] : null, 'id') ??
    (() => {
      // Legacy DSR screens sometimes keep these in sessionStorage.
      try {
        const raw = sessionStorage.getItem('sowId');
        return raw != null ? Number(raw) : null;
      } catch (_) {
        return null;
      }
    })();
  const selectedLogDateStr = selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null;
  const currentLogDateStr =
    currentDsrLog?.logDate ? moment(currentDsrLog.logDate).format('YYYY-MM-DD') : null;

  // Ensure a DSR log exists for the selected date when using the PQT tab/section.
  // (DSRReportDetails normally does this, but PQT can be opened directly.)
  const ensureLogKeyRef = React.useRef('');
  useEffect(() => {
    if (!projectId || !sowId || !selectedLogDateStr) return;
    const key = `${projectId}-${sowId}-${selectedLogDateStr}`;
    if (ensureLogKeyRef.current === key) return;
    // If current log matches date+sow, no need to (re)create.
    const matches =
      String(currentDsrLog?.sowId ?? '') === String(sowId) &&
      String(currentLogDateStr ?? '') === String(selectedLogDateStr);
    if (matches) {
      ensureLogKeyRef.current = key;
      return;
    }
    ensureLogKeyRef.current = key;
    onCreateDsrLog({ projectId, sowId, logDate: selectedLogDateStr });
  }, [projectId, sowId, selectedLogDateStr, currentDsrLog?.sowId, currentLogDateStr, onCreateDsrLog]);

  const fetchPqtData = useCallback(() => {
    // Fetch is driven by selectedDate; DSRReportDetails might not be mounted (section view),
    // so currentDsrLog may still point to a previous date.
    if (!projectId || !selectedDate || !sowId) return;
    const logDate = moment(selectedDate).format('YYYY-MM-DD');
    let userEmail = '';
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      userEmail = user.email || '';
    } catch (_) {}
    onFetchDsrLogPqt({
      logDate,
      userEmail,
      sowId: currentDsrLog?.sowId ?? sowId,
      projectId,
    });
  }, [currentDsrLog?.id, currentDsrLog?.sowId, projectId, sowId, selectedDate, onFetchDsrLogPqt]);

  // Fetch on every mount and whenever fetchKey changes (after save)
  useEffect(() => {
    fetchPqtData();
  }, [fetchPqtData, fetchKey]);

  // When date / project / sow changes, clear local rows while loading the next payload.
  useEffect(() => {
    setLocalElements([]);
  }, [projectId, sowId, selectedDate]);

  // Sync local elements and comments from API response
  useEffect(() => {
    const elements = get(dsrLogPqt, 'elements', []);
    const sorted = [...elements].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    setLocalElements(
      sorted.map((el) => ({
        ...el,
        projectPqtReportElementId: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
        id: el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id,
        comments: el.comments ?? null,
        __tillSnapshot: computeTillSnapshotForRow(el),
        __pqtActualDaysAtSnapshot: toFiniteNumber(el.actualDays ?? el.ActualDays) ?? 0,
      }))
    );
  }, [dsrLogPqt]);

  const handleActualDaysChange = useCallback((projectPqtReportElementId, value) => {
    setLocalElements((prev) =>
      prev.map((el) => {
        const id = el.projectPqtReportElementId ?? el.id;
        if (id !== projectPqtReportElementId) return el;
        const bidDays = Number(el.bidDays) || 0;
        const actualDays = value != null && value !== '' ? Number(value) : null;
        const variance = actualDays != null ? actualDays - bidDays : null;
        return { ...el, actualDays, variance };
      })
    );
  }, []);

  const handleElementCommentsChange = useCallback((projectPqtReportElementId, next) => {
    setLocalElements((prev) =>
      prev.map((el) => {
        const id = el.projectPqtReportElementId ?? el.id;
        if (id !== projectPqtReportElementId) return el;
        const val = next != null ? String(next) : '';
        return { ...el, comments: val.trim() ? val : null };
      })
    );
  }, []);

  const getReportElementId = (el) => el.projectPqtReportElementId ?? el.projectPqtElementId ?? el.id;

  // Refetch after save completes and navigate back on success
  const prevUpdating = React.useRef(false);
  useEffect(() => {
    if (prevUpdating.current && !isDsrLogPqtUpdating) {
      setFetchKey((k) => k + 1);
      if (saveClickedRef.current && lastDsrLogPqtUpdateSuccess === true && onCancel) {
        saveClickedRef.current = false;
        onCancel();
      }
    }
    prevUpdating.current = isDsrLogPqtUpdating;
  }, [isDsrLogPqtUpdating, lastDsrLogPqtUpdateSuccess, onCancel]);

  const handleSave = useCallback(() => {
    if (!currentDsrLog?.id) {
      message.warning('DSR log not loaded. Please select date and SOW.');
      return;
    }
    const elements = localElements.map((el) => ({
      projectPqtElementId: el.projectPqtElementId ?? getReportElementId(el),
      actualDays: Number(el.actualDays ?? 0),
      comments: el.comments ?? null,
    }));
    if (elements.length === 0) {
      message.info('Enter at least one actual days value to save.');
      return;
    }
    saveClickedRef.current = true;
    onUpdateDsrLogPqt({
      dsrLogId: currentDsrLog.id,
      elements,
    });
  }, [currentDsrLog?.id, localElements, onUpdateDsrLogPqt]);

  const columns = [
    {
      title: 'PQT Activities',
      dataIndex: 'elementName',
      key: 'elementName',
      width: 360,
      render: (text) => (
        <span className="modern-dsr-pqt-cell-name">{text || '—'}</span>
      ),
    },
    {
      title: 'Bid No. of Days',
      dataIndex: 'bidDays',
      key: 'bidDays',
      align: 'center',
      width: 110,
      render: (val) => (val != null ? Number(val) : '—'),
    },
    {
      title: 'Actual Days',
      key: 'actualDays',
      align: 'center',
      width: 120,
      render: (_, record) => {
        const id = getReportElementId(record);
        return (
          <InputNumber
            className="modern-dsr-pqt-inputnum"
            min={0}
            step={1}
            controls={false}
            value={getActualDaysInputValue(record)}
            placeholder="0"
            onChange={(v) => handleActualDaysChange(id, v)}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: 'Comments',
      key: 'comments',
      render: (_, record) => {
        const id = getReportElementId(record);
        return (
          <Input
            className="modern-dsr-pqt-input"
            value={record.comments ?? ''}
            onChange={(e) => handleElementCommentsChange(id, e.target.value)}
            placeholder="Comments"
            style={{ width: '100%' }}
          />
        );
      },
    },
  ];

  const totals = localElements.reduce(
    (acc, el) => {
      const bid = Number(el.bidDays) || 0;
      const actualDays = getActualDaysInputValue(el);
      acc.bid += bid;
      if (actualDays != null) acc.actualDays += actualDays;
      return acc;
    },
    { bid: 0, actualDays: 0 }
  );

  return (
    <div className="modern-dsr-pqt-actual-page">
      <Spin
        spinning={isDsrLogPqtLoading}
        indicator={(
          <span className="modern-dsr-pqt-spin" aria-hidden>
            <Loader2 className="modern-dsr-pqt-spin-icon" size={28} strokeWidth={2} />
          </span>
        )}
        tip="Loading PQT data..."
        size="large"
      >
        <div className="modern-dsr-table-wrap" style={{ marginBottom: 24 }}>
          <Table
            rowKey={(r) => r.projectPqtElementId ?? r.projectPqtReportElementId ?? r.elementName ?? r.sortOrder}
            columns={columns}
            dataSource={localElements}
            pagination={false}
            size="small"
            scroll={{ x: 1000 }}
            className="modern-dsr-table pipeline-table"
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <span className="modern-dsr-pqt-cell-name">Total</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="center">
                    <span className="modern-dsr-pqt-cell-name">{totals.bid}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="center">
                    <span className="modern-dsr-pqt-cell-name">{totals.actualDays}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} />
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </div>
      </Spin>

      <div className="modern-dsr-pqt-form-footer">
        <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
        <CrceButton
          label={isDsrLogPqtUpdating ? 'Saving…' : 'Save & Next'}
          variant="primary"
          size="medium"
          icon={Save}
          onClick={handleSave}
          disabled={isDsrLogPqtUpdating || !currentDsrLog?.id || isDsrLogPqtLoading}
        />
      </div>
    </div>
  );
};

const EMPTY_DSR_LOG_PQT = { pqt: null, elements: [] };

const mapStateToProps = (state) => ({
  currentDsrLog: get(state, 'dsrReducer.currentDsrLog'),
  dsrLogPqt: get(state, 'projectPqtReportsReducer.dsrLogPqt') ?? EMPTY_DSR_LOG_PQT,
  isDsrLogPqtLoading: get(state, 'projectPqtReportsReducer.isDsrLogPqtLoading', false),
  isDsrLogPqtUpdating: get(state, 'projectPqtReportsReducer.isDsrLogPqtUpdating', false),
  lastDsrLogPqtUpdateSuccess: get(state, 'projectPqtReportsReducer.lastDsrLogPqtUpdateSuccess', null),
});

const mapDispatchToProps = {
  onFetchDsrLogPqt: fetchDsrLogPqt,
  onUpdateDsrLogPqt: updateDsrLogPqt,
  onCreateDsrLog: createDsrLog,
};

export default connect(mapStateToProps, mapDispatchToProps)(PqtActualValuesPage);
